import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { processApi, documentApi } from '../api/services';
import { DocumentPreviewButton } from '../features/documents';

const categoryLabels: Record<string, string> = {
  APPLICATION_FORM: 'Antragsformular',
  IDENTITY_DOCUMENT: 'Ausweisdokument',
  PROOF_OF_INCOME: 'Einkommensnachweis',
  PROOF_OF_RESIDENCE: 'Wohnsitznachweis',
  SUPPORTING_DOCUMENT: 'Begleitdokument',
  DECISION_NOTICE: 'Bescheid',
  CORRESPONDENCE: 'Korrespondenz',
  OTHER: 'Sonstiges',
};

export function DocumentsPage() {
  const navigate = useNavigate();

  const { data: processesData, isLoading: processesLoading } = useQuery({
    queryKey: ['processes', true],
    queryFn: () => processApi.getProcesses(true),
  });

  const processes = processesData?.processes || [];

  const { data: allDocuments, isLoading: documentsLoading } = useQuery({
    queryKey: ['all-documents', processes.map((p) => p.id)],
    queryFn: async () => {
      const docs = await Promise.all(
        processes.map((p) => documentApi.getDocumentsByProcess(p.id))
      );
      return docs.flat();
    },
    enabled: processes.length > 0,
  });

  const isLoading = processesLoading || documentsLoading;

  const downloadDocument = async (docId: string, fileName: string) => {
    const blob = await documentApi.downloadDocument(docId);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const documents = allDocuments || [];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dokumente
      </Typography>

      {documents.length === 0 ? (
        <Card>
          <CardContent>
            <Alert severity="info">
              Keine Dokumente vorhanden
            </Alert>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Dateiname</TableCell>
                <TableCell>Kategorie</TableCell>
                <TableCell>Groesse</TableCell>
                <TableCell>Hochgeladen am</TableCell>
                <TableCell>Hochgeladen von</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((doc) => (
                <TableRow key={doc.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2">{doc.fileName}</Typography>
                    {doc.description && (
                      <Typography variant="caption" color="text.secondary">
                        {doc.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={categoryLabels[doc.category] || doc.category}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {(doc.fileSize / 1024).toFixed(1)} KB
                  </TableCell>
                  <TableCell>
                    {new Date(doc.uploadedAt).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </TableCell>
                  <TableCell>{doc.uploadedBy}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Prozess anzeigen">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/processes/${doc.processInstanceId}`)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <DocumentPreviewButton document={doc} />
                    <Tooltip title="Herunterladen">
                      <IconButton
                        size="small"
                        onClick={() => downloadDocument(doc.id, doc.fileName)}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
